import { makeStyles } from "@material-ui/core/styles";
import { Card, CardContent, Divider, Typography } from "@material-ui/core";
import LabelChip from "../chip/labelChip";
import DepositIcon from "../icons/depositIcon";
import WithdrawIcon from "../icons/withdrawIcon";
import { DEPOSIT_CURRENCIES, TRANSACTION_CATEGORIES, TRANSACTION_TYPES } from "../../utils/types";
import ModalConsumer from "../consumer/modalConsumer";
import ActionIconButton from "../button/actionIconButton";
import { formatCurrency } from "../../utils/formatter";
import PendingTransactionDialog from "../dialog/pendingTransactionDialog";
import Role from "../../utils/role";

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        marginBottom: '24px',
        backgroundColor: theme.surface.primary,
    },
    content: {
        width: '100%',
        padding: '24px'
    },
    primaryContent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
    },
    secondaryContent: {

    },
    currency: {
        marginBottom: '16px'
    },
    actionsContainer: {
        display: 'flex',
        marginTop: '6px'
    },
    balanceBreakdown: {
        marginTop: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    breakdownItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: theme.palette.text.secondary,
        '& .label': {
            opacity: 0.7,
        },
        '& .value': {
            fontWeight: 500,
            color: theme.palette.text.primary,
        }
    }
}));

const CustomerBalanceCard = (props) => {
    const { customerBalance, onchainBalance, otcBalance, totalInterest, activityListRef, accountId, customerId, onRefreshCustomer } = props
    const classes = useStyles();
    
    // Show breakdown only if we have separate balance data
    const showBreakdown = onchainBalance !== undefined && otcBalance !== undefined;
    
    return (
        <Card elevation={0} className={classes.root}>
            <CardContent className={classes.content}>
                <div className={classes.primaryContent}>
                    <div>
                        <Typography variant="h1" className={classes.currency}>
                            {formatCurrency(customerBalance, 'USD', false)}
                        </Typography>
                        <LabelChip variant="secondary" label="Total balance" />
                        {showBreakdown && (
                            <div className={classes.balanceBreakdown}>
                                <div className={classes.breakdownItem}>
                                    <span className="label">On-chain:</span>
                                    <span className="value">{formatCurrency(onchainBalance, 'USD', false)}</span>
                                </div>
                                <div className={classes.breakdownItem}>
                                    <span className="label">OTC:</span>
                                    <span className="value">{formatCurrency(otcBalance, 'USD', false)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                    <ModalConsumer>
                        {({ showModal }) => (
                            <div className={classes.actionsContainer}>
                                {Role.hasAdminRole() && (
                                    <>
                                        <ActionIconButton
                                            onClick={() => showModal(PendingTransactionDialog, {
                                                accountId,
                                                customerId,
                                                transactionType: TRANSACTION_TYPES.PENDING_DEBIT,
                                                transactionCategory: TRANSACTION_CATEGORIES.WITHDRAWAL,
                                                selectableCurrencies: ["USD"],
                                                onComplete: () => {
                                                    activityListRef?.current?.refresh();
                                                    onRefreshCustomer && onRefreshCustomer();
                                                }
                                            })}
                                            label="Withdraw"
                                            icon={<WithdrawIcon />}
                                        />
                                        <ActionIconButton
                                            onClick={() => showModal(PendingTransactionDialog, {
                                                accountId,
                                                customerId,
                                                transactionType: TRANSACTION_TYPES.PENDING_CREDIT,
                                                transactionCategory: TRANSACTION_CATEGORIES.DEPOSIT,
                                                onComplete: () => {
                                                    activityListRef?.current?.refresh();
                                                    onRefreshCustomer && onRefreshCustomer();
                                                },
                                                selectableCurrencies: Object.values(DEPOSIT_CURRENCIES),
                                            })}
                                            label="Deposit"
                                            icon={<DepositIcon />}
                                        />
                                    </>
                                )}
                                {(Role.hasAdminRole() || Role.hasExternalOpsRole()) && (
                                    <>
                                        <ActionIconButton
                                            onClick={() => showModal(PendingTransactionDialog, {
                                                accountId,
                                                customerId,
                                                transactionType: TRANSACTION_TYPES.PENDING_CREDIT,
                                                transactionCategory: 'Top up',
                                                onComplete: () => {
                                                    activityListRef?.current?.refresh();
                                                    onRefreshCustomer && onRefreshCustomer();
                                                },
                                                selectableCurrencies: Object.values(DEPOSIT_CURRENCIES),
                                            })}
                                            label="Top up (OTC)"
                                            icon={<DepositIcon />}
                                        />
                                        <ActionIconButton
                                            onClick={() => showModal(PendingTransactionDialog, {
                                                accountId,
                                                customerId,
                                                transactionType: TRANSACTION_TYPES.SEND_FIAT,
                                                transactionCategory: 'Send',
                                                onComplete: () => {
                                                    activityListRef?.current?.refresh();
                                                    onRefreshCustomer && onRefreshCustomer();
                                                },
                                                selectableCurrencies: ["USD"],
                                            })}
                                            label="Send (OTC)"
                                            icon={<WithdrawIcon />}
                                        />
                                    </>
                                )}
                            </div>
                        )}
                    </ModalConsumer>
                </div>
            </CardContent>
        </Card>
    );
};

export default CustomerBalanceCard;
